import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { DEFAULT_LANGUAGE, parseRoute, samePageIn, type Language } from "../lib/route";
import da from "./da.json";
import en from "./en.json";

/**
 * The URL decides the language, not localStorage.
 *
 * Every page exists at a Danish and an English URL, each declaring the other as
 * its `hreflang` alternate. If a stored preference could quietly render English
 * copy at a Danish URL, those declarations would be lies and a shared link would
 * open in the wrong language. So the path wins.
 */
const route = typeof window === "undefined" ? null : parseRoute(window.location.pathname);

function storedLanguage(): Language | null {
  try {
    const v = localStorage.getItem("lang");
    return v === "da" || v === "en" ? v : null;
  } catch {
    return null;
  }
}

const pathLanguage: Language = route?.language ?? DEFAULT_LANGUAGE;

// A stored preference only applies to the bare paths, which carry no language of
// their own. An explicit `/en/...` URL is a statement of intent — usually someone
// else's, in a shared link — and bouncing it back to Danish because of a months-old
// preference would be worse than ignoring the preference. Crawlers have no
// localStorage, so they always get exactly the language the URL promised.
if (route && route.kind !== "admin" && route.language === DEFAULT_LANGUAGE) {
  const stored = storedLanguage();
  if (stored && stored !== DEFAULT_LANGUAGE) {
    window.location.replace(samePageIn(route, stored) + window.location.search + window.location.hash);
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    da: { translation: da },
    en: { translation: en },
  },
  lng: pathLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: { escapeValue: false },
});

// Keep <html lang> in sync from the very first render — the prerendered pages
// already ship the right one, but the SPA fallback ships Danish.
if (typeof document !== "undefined") {
  document.documentElement.lang = pathLanguage;
}

/**
 * Switch language by going to the other language's URL.
 *
 * Deliberately a navigation rather than an in-place `changeLanguage`: the URL is
 * the source of truth, so changing the language without changing the URL would
 * put the page out of sync with its own canonical tag. The preference is still
 * stored, so the next visit to a bare link lands in the chosen language.
 */
export function setLang(language: Language): void {
  try {
    localStorage.setItem("lang", language);
  } catch {
    /* ignore */
  }
  if (!route) return;
  const target = samePageIn(route, language) + window.location.search + window.location.hash;
  if (target !== window.location.pathname + window.location.search + window.location.hash) {
    window.location.assign(target);
  }
}

export default i18n;
